
import type React from "react"

import { useRef, useEffect, useState } from "react"
import { motion, useInView, useAnimation } from "framer-motion"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Star } from "lucide-react"

export function AppleStyleTestimonials() {
  const [activeIndex, setActiveIndex] = useState(0)
  const controls = useAnimation()
  const containerRef = useRef<HTMLDivElement>(null)
  const carouselRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(containerRef, { once: true, amount: 0.3 })
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isMobile, setIsMobile] = useState(false)
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)

  // Add a new state for autoplay interval
  const [autoplayInterval, setAutoplayInterval] = useState<NodeJS.Timeout | null>(null)

  const heading = "What Our Customers Say"

  const testimonials = [
    {
      rating: 5,
      text: "AvePoint's solutions have transformed how we manage our data security. The compliance features are exactly what we needed to meet FedRAMP requirements.",
      author: "Sarah Johnson",
      role: "IT Director, Federal Agency",
      title: "Government IT Director",
      avatar: "/government-official-avatar.png",
    },
    {
      rating: 4, // Changed from 5 to 4
      text: "The migration to AvePoint was seamless. Their platform has significantly improved our data governance while reducing our operational costs.",
      author: "Michael Chen",
      role: "CIO, State Government",
      title: "State Government CIO",
      avatar: "/government-cio-avatar.png",
    },
    {
      rating: 3, // Changed from 5 to 3
      text: "AvePoint's security features give us peace of mind. Their platform helps us maintain compliance while providing excellent protection against threats.",
      author: "David Rodriguez",
      role: "Security Manager, Municipal Government",
      title: "Security Manager",
      avatar: "/security-manager-avatar.png",
    }
  ]

  // Check if we're on mobile
  // Modify the useEffect that checks for mobile to also set up autoplay
  useEffect(() => {
    const checkIfMobile = () => {
      const isMobileView = window.innerWidth < 768
      setIsMobile(isMobileView)

      // Clear any existing interval when screen size changes
      if (autoplayInterval) {
        clearInterval(autoplayInterval)
        setAutoplayInterval(null)
      }

      // Set up autoplay only on mobile
      if (isMobileView) {
        const interval = setInterval(() => {
          goToSlide(currentSlide + 1)
        }, 5000) // Change slide every 5 seconds
        setAutoplayInterval(interval)
      }
    }

    // Initial check
    checkIfMobile()

    // Add event listener
    window.addEventListener("resize", checkIfMobile)

    // Cleanup
    return () => {
      window.removeEventListener("resize", checkIfMobile)
      if (autoplayInterval) {
        clearInterval(autoplayInterval)
      }
    }
  }, [currentSlide]) // Add currentSlide as dependency to update interval when slide changes

  // Add a cleanup effect for the autoplay interval when component unmounts
  useEffect(() => {
    return () => {
      if (autoplayInterval) {
        clearInterval(autoplayInterval)
      }
    }
  }, [autoplayInterval])

  // GSAP animation for the cards when they come into view
  useEffect(() => {
    if (typeof window !== "undefined" && window.gsap && containerRef.current && isInView) {
      if (isMobile) {
        // Mobile animations
        const currentCard = carouselRef.current?.querySelector(`.testimonial-slide[data-index="${currentSlide}"]`)

        if (currentCard) {
          window.gsap.fromTo(
            currentCard,
            {
              opacity: 0,
              scale: 0.95,
            },
            {
              opacity: 1,
              scale: 1,
              duration: 0.6,
              ease: "power2.out",
            },
          )
        }
      } else {
        // Desktop animations
        const cards = containerRef.current.querySelectorAll(".testimonial-card")

        window.gsap.fromTo(
          cards,
          {
            y: 30,
            opacity: 0,
            scale: 0.95,
          },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            stagger: 0.1,
            duration: 0.8,
            ease: "power2.out",
          },
        )
      }
    }
  }, [isInView, currentSlide, isMobile])

  // Handle slide navigation
  const goToSlide = (index: number) => {
    if (index < 0) {
      setCurrentSlide(testimonials.length - 1)
    } else if (index >= testimonials.length) {
      setCurrentSlide(0)
    } else {
      setCurrentSlide(index)
    }
  }

  // Handle touch events for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 50) {
      // Swipe left
      goToSlide(currentSlide + 1)
    }

    if (touchStart - touchEnd < -50) {
      // Swipe right
      goToSlide(currentSlide - 1)
    }
  }

  // Animate elements when they come into view
  useEffect(() => {
    if (isInView) {
      controls.start("visible")
    }
  }, [controls, isInView])

  // Variants for staggered animations
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3, // 控制每个卡片之间的间隔（比内部动画长）
      },
    },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: [0.25, 0.1, 0.25, 1.0], // Apple's custom easing
      },
    },
  }

  // Handle navigation
  const handlePrev = () => {
    setActiveIndex((prev) => (prev === 0 ? 1 : 0))
  }

  const handleNext = () => {
    setActiveIndex((prev) => (prev === 1 ? 0 : 1))
  }
  const renderDesktopView = () => (
    <>
      <motion.h2
        className="text-2xl md:text-3xl font-bold text-center mb-6 md:mb-10"
        variants={containerVariants}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >{heading}</motion.h2>
      <div ref={containerRef} className="hidden md:block relative px-4 md:px-6 mx-auto max-w-7xl">
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}>
          {testimonials.map((testimonial, index) => (
            <>
              <motion.div
                key={index}
                className="testimonial-card bg-white rounded-xl p-6 shadow-lg border border-gray-50 flex flex-col h-full transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                variants={containerVariants}
              >
                {/* Star Rating */}
                <motion.div className="flex mb-4" animate={{ opacity: 1, y: 0 }} variants={itemVariants}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`h-4 w-4 ${i < testimonial.rating ? "text-yellow-400 fill-yellow-400" : "text-gray-200"}`}
                    />
                  ))}
                </motion.div>

                {/* Testimonial Text */}
                <motion.p className="text-sm mb-5 leading-relaxed" animate={{ opacity: 1, y: 0 }} variants={itemVariants}>"{testimonial.text}"</motion.p>

                {/* Position and Agency */}
                <motion.div className="mt-auto" animate={{ opacity: 1, y: 0 }} variants={itemVariants}>
                  <p className="font-medium text-gray-900 text-sm">{testimonial.role.split(", ")[0]}</p>
                  <p className="text-xs text-gray-500">{testimonial.role.split(", ")[1]}</p>
                </motion.div>
              </motion.div>
            </>
          ))}
        </motion.div>
      </div>
    </>
  )

  const renderMobileView = () => (
    <>
      <h2
        className="text-2xl md:text-3xl font-bold text-center mb-6 md:mb-10"
      >{heading}</h2>
      <div
        className="md:hidden px-4 mx-auto max-w-md"
        ref={carouselRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Carousel container with overflow hidden */}
        <div className="relative overflow-hidden">
          {/* Testimonial slides */}
          <div
            className="flex transition-transform duration-300 ease-out"
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          >
            {testimonials.map((testimonial, index) => (
              <motion.div key={index} data-index={index} className={`testimonial-slide w-full flex-shrink-0 px-6 pb-6`}
              initial={{ opacity: 0, y: 20 }}
                animate={{
                  opacity: activeIndex === index ? 1 : 0.7,
                  y: 0,
                  scale: activeIndex === index ? 1 : 0.95,
                }}
                transition={{ duration: 0.5 }}
            >
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-50 flex flex-col">
                  {/* Star Rating */}
                  <div className="flex mb-4 justify-center">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className={`h-4 w-4 ${i < testimonial.rating ? "text-yellow-400 fill-yellow-400" : "text-gray-200"}`}
                      />
                    ))}
                  </div>

                  {/* Testimonial Text */}
                  <p className="text-center text-base text-gray-700 mb-6 leading-relaxed">"{testimonial.text}"</p>

                  {/* Position and Agency - centered layout */}
                  <div className="text-center mt-auto">
                    <p className="font-medium text-gray-900 text-sm">{testimonial.role.split(", ")[0]}</p>
                    <p className="text-xs text-gray-500">{testimonial.role.split(", ")[1]}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* REMOVE THESE NAVIGATION ARROWS */}
          <button
            onClick={() => goToSlide(currentSlide - 1)}
            className="absolute left-0 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-full shadow-md z-10 text-gray-700"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <button
            onClick={() => goToSlide(currentSlide + 1)}
            className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-full shadow-md z-10 text-gray-700"
            aria-label="Next testimonial"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Pagination indicators - Apple-style dots */}
        <div className="flex justify-center mt-6 space-x-2">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                currentSlide === index ? "bg-[#ef483d] w-4" : "bg-gray-300"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </>
  )

  return isMobile ? renderMobileView() : renderDesktopView()
}

